/**
 * Which agent answers on which page, and what they can do there.
 *
 * The rail is always the same surface; only who is behind it changes, so the
 * agent you get is the one who owns the work in front of you. Lykke is the
 * default because she is the one who routes and delegates — on a page with no
 * obvious owner, "ask the chief of staff" is the right behaviour.
 *
 * Agent ids match the directory names under cowork-cloud/backend/src/agents.
 * Only route to an agent whose AGENT.md has `isActive: true` — Jones, Nova and
 * Joanya are defined but switched off, and routing a page to one of them would
 * put an agent in the rail who never answers.
 */
import type { CurrentPage } from '@/routes';

export const DEFAULT_AGENT_ID = 'account-manager'; // Lykke

const AGENT_BY_PAGE: Partial<Record<CurrentPage, string>> = {
  // Campaign execution — Saga runs the brief→delivery flow
  'see-campaigns': 'campaign-manager',
  campaigns: 'campaign-manager',
  'campaign-calendar': 'campaign-manager',
  drafts: 'campaign-manager',
  casting: 'campaign-manager',
  'ambassador-hub': 'campaign-manager',
  strategies: 'campaign-manager',
  'campaign-plan': 'campaign-manager',

  // Discovery — Rex sources and screens creators
  creators: 'rex-influencer',
  'search-results': 'rex-influencer',
  'creator-profile': 'rex-influencer',
  'discovery-creatorlist': 'rex-influencer',
  creatorslist: 'rex-influencer',
  'creatorlist-detail': 'rex-influencer',

  // Measurement — Kai reads the datalake
  performance: 'kai-analytics',
  'brand-lift': 'kai-analytics',
  'marketing-mix-model-mmm': 'kai-analytics',
  'audience-insights': 'kai-analytics',
  'brand-pulse': 'kai-analytics',
  trends: 'kai-analytics',
  'news-monitor': 'kai-analytics',
  'agent-dashboards': 'kai-analytics',
  'agent-reports': 'kai-analytics',
  'agent-competitor-ads': 'kai-analytics',
  'agent-ads-library': 'kai-analytics',
  'agent-radar-library': 'kai-analytics',
  'agent-tiktok-trends': 'kai-analytics',

  // Content — Aria
  'content-hub': 'aria-content',
  'ad-studios': 'aria-content',
  'video-ad-studio': 'aria-content',
  'audio-dubbing': 'aria-content',
  'audio-studio': 'aria-content',

  // Paid — Lena is the one who cares what a connected ad account is spending
  'ad-accounts': 'leo-perf',
};

/** Pages that carry their own assistant: the global rail stays away (Deck studio is Lykke's — one assistant per screen). */
export const PAGES_WITH_OWN_AGENT: ReadonlySet<string> = new Set(['agent-reports', 'agent-video-studio']);

export function agentForPage(page: CurrentPage): string {
  return AGENT_BY_PAGE[page] ?? DEFAULT_AGENT_ID;
}

/**
 * A one-line description of where the user is, sent with each message so the
 * agent can answer about *this* campaign / list without being told which.
 */
export function describePageContext(params: {
  page: CurrentPage;
  pathname: string;
  campaignId?: string;
  listId?: string;
  creatorId?: string;
}): string {
  const bits = [`The user is on the "${params.page}" page (${params.pathname}).`];
  if (params.campaignId) bits.push(`Campaign in view: ${params.campaignId}.`);
  if (params.listId) bits.push(`Creator list in view: ${params.listId}.`);
  if (params.creatorId) bits.push(`Creator profile in view: ${params.creatorId}.`);
  return bits.join(' ');
}

/**
 * What this agent can actually do here.
 *
 * "Hand over the work entirely" is an empty promise if nothing says what the
 * work could be — a new user has no way to guess that "take this to 20 Swedish
 * creators" is in scope. These are openers, not templates: they name real
 * capabilities on the page you are standing on.
 *
 * Deliberately a total `Record`, not a `Partial`: a page added to `CurrentPage`
 * without openers falls back to a generic prompt that reads as an empty
 * promise, and nothing would catch it. This way tsc does.
 *
 * Only promise what the tools can do. Agents cannot change account settings,
 * connect ad accounts or invite teammates, so those pages get openers about
 * what the agents *are* doing rather than a request they will have to refuse.
 */
const STARTERS: Record<CurrentPage, string[]> = {
  dashboard: [
    'What needs me today?',
    'Find creators',
    'Start a campaign',
    'How did we do?',
  ],

  // ── Campaigns ────────────────────────────────────────────────────────────
  'see-campaigns': [
    'Where are we and what is blocking?',
    'Draft the brief for this campaign',
    'Find 20 creators that fit and add them',
    'Summarise this for the client',
  ],
  campaigns: [
    'Set up a campaign for this brand',
    'Copy the setup from our last campaign',
    'Which campaigns are waiting on me?',
  ],
  'campaign-plan': [
    'Why this creator size band?',
    'What did we learn last campaign?',
    'Suggest a sharper concept',
  ],
  'campaign-calendar': [
    'What is going live this month?',
    'Anything at risk of slipping?',
    'Space these launches out sensibly',
  ],
  strategies: [
    'Turn this strategy into a campaign',
    'What did we learn from the last one?',
  ],
  drafts: [
    'Review these drafts against the brief',
    'Which of these are waiting on my approval?',
    'Write feedback for the ones that miss',
  ],
  casting: [
    'Who should we cast for this?',
    'Screen this shortlist for brand safety',
    'Why did we pick these creators?',
  ],
  'ambassador-hub': [
    'Who should we re-book?',
    'Which ambassadors performed best?',
  ],

  // ── Discovery ────────────────────────────────────────────────────────────
  creators: [
    'Find 20 creators that fit our brand',
    'Screen this list for brand safety',
    'Who did we work with before?',
  ],
  'search-results': [
    'Narrow this to the ones that actually fit',
    'Save the best of these as a list',
    'Which of these have we worked with?',
  ],
  'creator-profile': [
    'Is this creator a fit for us?',
    'What did they deliver last time?',
    'Add them to a campaign',
  ],
  'discovery-creatorlist': [
    'Find more creators like these',
    'Turn this list into a campaign',
  ],
  creatorslist: [
    'Turn this list into a campaign',
    'Build me a list for our next launch',
  ],
  'creatorlist-detail': [
    'Turn this list into a campaign',
    'Screen this list for brand safety',
    'Find more creators like these',
  ],

  // ── Measurement ──────────────────────────────────────────────────────────
  performance: [
    'What worked last quarter?',
    'Build me a client-ready report',
    'Which creators earned their fee?',
  ],
  'brand-lift': [
    'Did our campaigns move brand metrics?',
    'Build a lift summary for the client',
  ],
  'marketing-mix-model-mmm': [
    'Where should the next budget go?',
    'Which beat types paid off for us?',
  ],
  'audience-insights': [
    'Who are we actually reaching?',
    'Which creators reach the audience we want?',
  ],
  'brand-pulse': [
    'What are people saying about us?',
    'Anything I should worry about?',
  ],
  trends: [
    'What are creators in our space posting about?',
    'Build a campaign around a trend',
  ],
  'news-monitor': [
    'Summarise what matters to us',
    'Anything our competitors are doing?',
  ],

  // ── Content ──────────────────────────────────────────────────────────────
  'content-hub': [
    'Draft posts from these assets',
    'Does this fit our brand voice?',
    'Make variants for another market',
  ],
  'ad-studios': [
    'Generate ad creative for this campaign',
    'Write the script for a 30-second ad',
  ],
  'video-ad-studio': [
    'Write the script for this ad',
    'Suggest three hooks for the opening',
  ],
  'audio-dubbing': [
    'Which markets should we localise for?',
    'Adapt this script for another language',
  ],
  'audio-studio': [
    'What kind of music fits this campaign?',
    'Write a prompt for a 15-second TikTok soundtrack',
  ],

  // ── Paid & accounts ──────────────────────────────────────────────────────
  'ad-accounts': [
    'What are we spending, and on what?',
    'Which connected account is underperforming?',
  ],

  // ── Everything else — honest openers, nothing the tools cannot do ────────
  messages: [
    'Which creators are waiting on us?',
    'Draft outreach for a campaign',
  ],
  trophies: [
    'Which campaign should we submit?',
    'Write the case study for this one',
  ],
  settings: [
    'Which brand are you working for right now?',
    'What can you do without asking me?',
  ],
  team: [
    'What is each agent working on?',
    'Who should I hand this to?',
  ],
  'dev-mode': ['What are you working on?'],

  // ── Agent surfaces ───────────────────────────────────────────────────────
  'agent-chats': [
    'Pick up where we left off',
    'What did we decide last time?',
  ],
  'agent-tasks': [
    'What is running right now?',
    'Anything stuck or waiting on me?',
  ],
  'agent-approvals': [
    'What is waiting for my approval?',
    'Explain this Meta shells plan before I approve it',
  ],
  'agent-team': [
    'Who should I hand this to?',
    'What is each of you good at?',
  ],
  'agent-integrations': [
    'What can you reach today?',
    'What would you do with more access?',
  ],
  'agent-brand': [
    'What is missing from our Brand OS?',
    'Research our competitors',
    'Does our campaign work match this?',
  ],
  'agent-brand-pulse': [
    'What changed for the brand this week?',
    'What is missing from our Brand OS?',
  ],
  'agent-brand-id': [
    'Does our latest campaign match the brand voice?',
    'What should the brand identity say that it does not?',
  ],
  'agent-brand-promotables': [
    'Which product should the next campaign push?',
    'Add a promotable from our site',
  ],
  'agent-brand-audiences': [
    'Who are we not reaching yet?',
    'Which audience fits this campaign best?',
  ],
  'agent-brand-competitors': [
    'Research our competitors',
    'What are competitors doing with creators right now?',
  ],
  'agent-competitor-ads': [
    'What are our competitors pushing right now?',
    'Which of these ads would work as creator content?',
    'Summarise the gaps nobody is covering',
  ],
  'agent-radar-library': [
    'Which kept ads should go in the next proposal?',
    'What formats dominate what we have captured?',
    'Summarise the hooks across these ads',
  ],
  'agent-ads-library': [
    'What is this advertiser running on Instagram right now?',
    'Which of these ads are creator-led?',
    'What hooks and offers repeat across these ads?',
  ],
  'agent-knowledge': [
    'What changed on TikTok this month?',
    'Which platform updates matter for our next proposal?',
  ],
  'agent-tiktok-trends': [
    'Which trends fit our brand this month?',
    'Give me three creator ideas on the top hashtag',
    'What are competitors doing on TikTok?',
  ],
  'agent-brand-library': [
    'What do you know about our brand?',
    'Where did this come from?',
  ],
  'agent-wiki': [
    'What do you know about our brand?',
    'Where did this come from?',
  ],
  'agent-deliverables': [
    'Build me a client-ready deck',
    'Turn the last campaign into a report',
  ],
  'agent-dashboards': [
    'Build a live dashboard of all our campaigns',
    'Which creators performed best this quarter?',
    'Make this dashboard refresh daily and email it to me',
  ],
  'agent-reports': [
    'Write a report on last quarter\'s campaigns — what worked and why',
    'Turn the campaign dashboard into a slide deck for my team',
    'Summarise creator performance with recommendations',
  ],
  'agent-video-studio': [
    'Cut an ad from this campaign\'s best clips',
    'Make a 20-second recap of the campaign',
  ],
  'agent-desk': [
    'What needs me right now?',
    'Run the draft pre-reviews',
    'Who is overdue and needs a chase?',
  ],
  // Moved to the back office (2026-09-28); the page only forwards there.
  'agent-kickoff': [],
  'agent-media-plans': [],
  'agent-canvas': [
    'What is on this board?',
    'Turn the selected cards into a brief',
    'Which competitor ad should we answer first?',
  ],
};

export function startersForPage(page: CurrentPage): string[] {
  return STARTERS[page] ?? ['What needs me right now?', 'What are you working on?'];
}
